// Холст дашборда: масштаб, адаптивная раскладка сетки, перерисовка визуализаций при ресайзе.

import type { GridStackWidget } from 'gridstack';

import type { WidgetDefinition } from '../types';
import { t } from '../i18n';

import { controls } from './controls';
import { currentLocale, setIconButton } from './locale';
import { displayedWidget, effectiveData, findWidget, resizeWidgetVisual, visualContainer } from './widgets';
import { grid } from './grid';
import { loadedChartModule } from './visual-loader';
import { markDirty, setStatus } from './status';
import { minimumWidgetHeight } from './widget-form';
import { state } from './state';
import { syncSelectedWidgetClass } from './workspace';

export const desktopFixedCanvasMedia = window.matchMedia('(min-width: 901px)');

export const DESKTOP_CANVAS_MIN_WIDTH = 1180;

export const DESKTOP_CANVAS_MIN_SCALE = 0.72;

export type GridNodeLike = {
  id?: string;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
};

export type GridElementLike = HTMLElement & { gridstackNode?: GridNodeLike };

export let lastAppliedGridColumns = 0;

/** Число колонок, под которое разложена сетка; 0 — разложить заново при следующем проходе. */
export function setLastAppliedGridColumns(columns: number): void {
  lastAppliedGridColumns = columns;
}

export let currentCanvasScale = 1;

export const narrowWorkspaceMedia = window.matchMedia('(max-width: 900px)');

export const phoneWorkspaceMedia = window.matchMedia('(max-width: 560px)');

export const pendingVisualResizes = new Set<string>();

export let visualResizeFrame = 0;

export let gridMetricsFrame = 0;

export const visualResizeObserver = typeof ResizeObserver === 'undefined'
  ? null
  : new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const id = entry.target.id.startsWith('visual-') ? entry.target.id.slice('visual-'.length) : '';
        if (id) scheduleVisualResize(id);
      });
    });

export const gridMetricsObserver = typeof ResizeObserver === 'undefined'
  ? null
  : new ResizeObserver(() => scheduleGridMetricsUpdate());

export function scheduleVisualResize(id: string): void {
  pendingVisualResizes.add(id);
  if (visualResizeFrame) return;
  visualResizeFrame = window.requestAnimationFrame(() => {
    visualResizeFrame = 0;
    const ids = [...pendingVisualResizes];
    pendingVisualResizes.clear();
    ids.forEach((id) => {
      const container = visualContainer(id);
      // Скрытый или ещё не разложенный контейнер (0×0): ECharts падает на
      // графах в системе координат View, а ширина 0 ложно включала бы
      // компактный режим. Когда контейнер получит размер, ResizeObserver
      // вызовет перерисовку снова.
      if (container && (container.clientWidth === 0 || container.clientHeight === 0)) return;
      const widget = findWidget(id);
      const rawData = state.dataCache.get(id);
      const data = widget && rawData ? effectiveData(widget, rawData) : undefined;
      const compact = (container?.clientWidth ?? 0) < 520;
      const previousCompact = state.visualCompactModes.get(id);
      const isChart = widget
        && widget.visualization !== 'table'
        && widget.visualization !== 'choropleth'
        && widget.visualization !== 'map_symbols';
      if (container && widget && data && isChart && previousCompact !== undefined && previousCompact !== compact) {
        state.visualCompactModes.set(id, compact);
        loadedChartModule?.renderChart(container, displayedWidget(widget), data);
      } else {
        resizeWidgetVisual(id);
      }
    });
  });
}

export function scheduleAllVisualResizes(): void {
  state.definition.widgets.forEach((widget) => scheduleVisualResize(widget.id));
}

export function scheduleCanvasRefresh(delay = 260): void {
  window.setTimeout(() => {
    scheduleGridMetricsUpdate();
    scheduleAllVisualResizes();
  }, delay);
}

export function observeWidgetVisual(id: string): void {
  const container = visualContainer(id);
  if (container) visualResizeObserver?.observe(container);
}

export function unobserveWidgetVisual(id: string): void {
  const container = visualContainer(id);
  if (container) visualResizeObserver?.unobserve(container);
  pendingVisualResizes.delete(id);
}

export function columnNoun(value: number): string {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return 'колонка';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'колонки';
  return 'колонок';
}

export function updateWidgetSizeBadge(_item: GridElementLike): void {}

export function updateAllWidgetSizeBadges(): void {}

export function desktopFixedCanvas(): boolean {
  return desktopFixedCanvasMedia.matches;
}

export function canvasHorizontalPadding(): number {
  const style = window.getComputedStyle(controls.canvasScroll);
  return (Number.parseFloat(style.paddingLeft) || 0) + (Number.parseFloat(style.paddingRight) || 0);
}

export function updateDashboardCanvasScale(): number {
  if (!desktopFixedCanvas()) {
    currentCanvasScale = 1;
    controls.canvasScaleInner.style.width = '';
    controls.canvasScaleInner.style.transform = '';
    controls.canvasScaleShell.style.width = '';
    controls.canvasScaleShell.style.height = '';
    controls.canvasScroll.classList.remove('is-scaled-canvas', 'has-horizontal-canvas-scroll');
    return currentCanvasScale;
  }

  const paddingX = canvasHorizontalPadding();
  const availableWidth = Math.max(1, controls.canvasScroll.clientWidth - paddingX);
  const fullCanvasWidth = Math.max(availableWidth, controls.workspace.clientWidth - paddingX);
  const logicalWidth = Math.max(DESKTOP_CANVAS_MIN_WIDTH, Math.floor(fullCanvasWidth));
  const rawScale = Math.min(1, availableWidth / logicalWidth);
  const scale = rawScale < 1 ? Math.max(DESKTOP_CANVAS_MIN_SCALE, rawScale) : 1;
  const scaledWidth = Math.ceil(logicalWidth * scale);

  currentCanvasScale = scale;
  controls.canvasScaleInner.style.width = `${logicalWidth}px`;
  controls.canvasScaleInner.style.transform = scale < 0.999 ? `scale(${scale})` : '';
  controls.canvasScaleShell.style.width = `${scaledWidth}px`;
  controls.canvasScroll.classList.toggle('is-scaled-canvas', scale < 0.999);
  controls.canvasScroll.classList.toggle('has-horizontal-canvas-scroll', scaledWidth > availableWidth + 1);

  window.requestAnimationFrame(() => {
    const logicalHeight = Math.max(
      controls.gridRoot.offsetHeight,
      controls.canvasScaleInner.scrollHeight,
      controls.gridRoot.scrollHeight,
      574,
    );
    controls.canvasScaleShell.style.height = `${Math.ceil(logicalHeight * scale)}px`;
  });

  return currentCanvasScale;
}

export function responsiveLayoutFor(widget: WidgetDefinition, columns: number, cursor: { x: number; y: number; rowHeight: number }) {
  const height = Math.max(widget.layout.h, minimumWidgetHeight(widget.visualization));
  if (columns >= 12) {
    return {
      x: widget.layout.x,
      y: widget.layout.y,
      w: widget.layout.w,
      h: height,
    };
  }

  const minWidth = Math.min(3, columns);
  const isWideWidget = widget.layout.w >= Math.max(5, columns - 1);
  const width = columns <= 3 || isWideWidget
    ? columns
    : Math.min(columns, Math.max(minWidth, widget.layout.w));

  if (cursor.x > 0 && cursor.x + width > columns) {
    cursor.x = 0;
    cursor.y += Math.max(1, cursor.rowHeight);
    cursor.rowHeight = 0;
  }

  const layout = {
    x: cursor.x,
    y: cursor.y,
    w: width,
    h: height,
  };

  cursor.x += width;
  cursor.rowHeight = Math.max(cursor.rowHeight, height);
  if (cursor.x >= columns) {
    cursor.x = 0;
    cursor.y += Math.max(1, cursor.rowHeight);
    cursor.rowHeight = 0;
  }
  return layout;
}

export function applyResponsiveGridLayout(force = false): void {
  updateDashboardCanvasScale();
  if (desktopFixedCanvas() && grid.getColumn() !== 12) {
    grid.column(12, 'none');
  }
  const columns = desktopFixedCanvas() ? 12 : grid.getColumn();
  if (!force && columns === lastAppliedGridColumns) return;
  lastAppliedGridColumns = columns;
  const cursor = { x: 0, y: 0, rowHeight: 0 };

  const wasSuppressingGridEvents = state.suppressGridEvents;
  state.suppressGridEvents = true;
  grid.batchUpdate();
  // Раскладка должна получаться той же, что при открытии дашборда
  // (renderDashboardGrid ставит виджеты по одному в порядке определения).
  // Раньше виджеты переставлялись на месте, и каждый следующий сталкивался
  // с ещё не переставленными в старой раскладке — после ширины 390 → 1920
  // GridStack расталкивал их иначе, чем при загрузке, и порядок ломался до
  // перезагрузки. Поэтому сначала все уводятся вниз, за пределы раскладки,
  // а затем ставятся по порядку — сталкиваясь только с уже поставленными.
  const items = state.definition.widgets
    .map((widget) => ({ widget, item: gridItemForWidget(widget.id) }))
    .filter((entry): entry is { widget: WidgetDefinition; item: GridElementLike } => entry.item != null);
  let parkingY = Math.max(1000, grid.getRow() + 1);
  items.forEach(({ item }) => {
    grid.update(item, { x: 0, y: parkingY });
    parkingY += Math.max(1, item.gridstackNode?.h ?? 1);
  });
  items.forEach(({ widget, item }) => {
    grid.update(item, responsiveLayoutFor(widget, columns, cursor));
    updateWidgetSizeBadge(item);
  });
  grid.batchUpdate(false);
  state.suppressGridEvents = wasSuppressingGridEvents;
  syncSelectedWidgetClass();
}

export function updateGridMetrics(): void {
  const columns = desktopFixedCanvas() ? 12 : grid.getColumn();
  const rowHeight = Math.round(grid.getCellHeight(true));
  controls.gridRoot.style.setProperty('--grid-columns', String(columns));
  controls.gridRoot.style.setProperty('--grid-row-height', `${rowHeight}px`);
  controls.layoutStatus.textContent = desktopFixedCanvas() && !state.viewMode && currentCanvasScale < 0.999
    ? `Масштаб холста: ${Math.round(currentCanvasScale * 100)}%`
    : '';
  controls.layoutHelp.textContent = columns === 1
    ? t(currentLocale(), 'mobileLayoutHelp')
    : t(currentLocale(), 'layoutHelp');
  updateAllWidgetSizeBadges();
}

export function scheduleGridMetricsUpdate(): void {
  if (gridMetricsFrame) window.cancelAnimationFrame(gridMetricsFrame);
  gridMetricsFrame = window.requestAnimationFrame(() => {
    gridMetricsFrame = 0;
    updateDashboardCanvasScale();
    updateGridMetrics();
    applyResponsiveGridLayout();
    state.definition.widgets.forEach((widget) => scheduleVisualResize(widget.id));
  });
}

export function setGridInteraction(active: boolean, verb?: string, item?: GridElementLike): void {
  controls.gridRoot.classList.toggle('is-interacting', active);
  controls.canvasScroll.classList.toggle('is-grid-interacting', active);
  if (item) updateWidgetSizeBadge(item);
  if (active && item?.gridstackNode) {
    const node = item.gridstackNode;
    setStatus(`${verb ?? 'Изменение макета'}: ${node.w ?? 1}×${node.h ?? 1} ячеек`);
  }
}

export function setLayoutEditing(enabled: boolean): void {
  if (state.viewMode && enabled) enabled = false;
  state.layoutEditing = enabled;
  grid.enableMove(enabled);
  grid.enableResize(enabled);
  controls.gridRoot.classList.toggle('is-layout-editing', enabled);
  controls.canvasPanel.classList.toggle('is-layout-editing', enabled);
  controls.toggleLayout.setAttribute('aria-pressed', String(enabled));
  setIconButton(
    controls.toggleLayout,
    enabled ? t(currentLocale(), 'finishLayout') : t(currentLocale(), 'editLayout'),
    enabled ? 'check' : 'move',
  );
  if (!enabled) setGridInteraction(false);
}

export function syncLayoutsFromGrid(): void {
  // В определении хранится раскладка на 12 колонок. Узкая (3 или 1 колонка)
  // строится из неё на лету; записать её обратно значило бы испортить
  // настольную раскладку — после возврата к широкому окну виджеты
  // оставались бы стопкой до перезагрузки.
  if (grid.getColumn() !== 12) return;
  const savedLayout = grid.save(false, false) as GridStackWidget[];
  savedLayout.forEach((node) => {
    const widget = findWidget(String(node.id ?? ''));
    if (!widget) return;
    widget.layout = {
      x: node.x ?? widget.layout.x,
      y: node.y ?? widget.layout.y,
      w: node.w ?? widget.layout.w,
      h: node.h ?? widget.layout.h,
    };
  });
  updateAllWidgetSizeBadges();
}

export function widgetIdFromGridItem(target: Element): string | null {
  const item = target.closest<HTMLElement>('.grid-stack-item');
  return item?.getAttribute('gs-id')
    ?? item?.dataset.gsId
    ?? item?.querySelector<HTMLElement>('[data-widget-id]')?.dataset.widgetId
    ?? null;
}

export function gridItemForWidget(id: string): GridElementLike | null {
  return (
    controls.gridRoot.querySelector<GridElementLike>(`.grid-stack-item[gs-id="${CSS.escape(id)}"]`)
    ?? controls.gridRoot.querySelector<HTMLElement>(`[data-widget-id="${CSS.escape(id)}"]`)?.closest<GridElementLike>('.grid-stack-item')
    ?? null
  );
}

export function applyLayoutCommand(id: string, command: string): void {
  const item = gridItemForWidget(id);
  const node = item?.gridstackNode;
  const widget = findWidget(id);
  if (!item || !node || !widget) return;
  const columns = grid.getColumn();
  const current = {
    x: node.x ?? 0,
    y: node.y ?? 0,
    w: node.w ?? Math.min(6, columns),
    h: node.h ?? 4,
  };
  const minimumWidth = Math.min(3, columns);
  const minH = minimumWidgetHeight(widget.visualization);
  if (command === 'up') current.y = Math.max(0, current.y - 1);
  if (command === 'down') current.y += 1;
  if (command === 'left') current.x = Math.max(0, current.x - 1);
  if (command === 'right') current.x = Math.min(Math.max(0, columns - current.w), current.x + 1);
  if (command === 'narrower') current.w = Math.max(minimumWidth, current.w - 1);
  if (command === 'wider') current.w = Math.min(columns, current.w + 1);
  if (command === 'shorter') current.h = Math.max(minH, current.h - 1);
  if (command === 'taller') current.h += 1;
  current.x = Math.min(current.x, Math.max(0, columns - current.w));
  grid.update(item, current);
  syncLayoutsFromGrid();
  updateWidgetSizeBadge(item);
  scheduleVisualResize(id);
  markDirty();
  setStatus('Положение или размер виджета изменены по сетке', 'success');
}

export function autoConfigureDashboardLayout(): void {
  if (!state.definition.widgets.length) return;
  syncLayoutsFromGrid();
  const columns = desktopFixedCanvas() ? 12 : grid.getColumn();
  const sorted = [...state.definition.widgets].sort((a, b) =>
    (a.layout.y - b.layout.y) || (a.layout.x - b.layout.x),
  );
  type Rect = { id: string; x: number; y: number; w: number; h: number };
  const rects: Rect[] = [];
  const minWidth = Math.min(3, columns);
  const occupied = (x: number, y: number, w: number, h: number, ignoreId?: string): boolean => (
    rects.some((rect) => {
      if (rect.id === ignoreId) return false;
      return x < rect.x + rect.w && x + w > rect.x && y < rect.y + rect.h && y + h > rect.y;
    })
  );
  sorted.forEach((widget) => {
    const minH = minimumWidgetHeight(widget.visualization);
    const width = Math.max(minWidth, Math.min(columns, widget.layout.w));
    const height = Math.max(minH, widget.layout.h);
    let placed: Rect | null = null;
    for (let y = 0; !placed && y < 200; y += 1) {
      for (let x = 0; x <= columns - width; x += 1) {
        if (!occupied(x, y, width, height)) {
          placed = { id: widget.id, x, y, w: width, h: height };
          break;
        }
      }
    }
    rects.push(placed ?? { id: widget.id, x: 0, y: 0, w: width, h: height });
  });
  rects
    .sort((a, b) => (a.y - b.y) || (a.x - b.x))
    .forEach((rect) => {
      while (rect.x + rect.w < columns && !occupied(rect.x, rect.y, rect.w + 1, rect.h, rect.id)) rect.w += 1;
      while (rect.h < 10 && !occupied(rect.x, rect.y, rect.w, rect.h + 1, rect.id)) {
        const nextRowHasWidget = rects.some((other) =>
          other.id !== rect.id
          && other.y >= rect.y + rect.h
          && other.y < rect.y + rect.h + 2
          && rect.x < other.x + other.w
          && rect.x + rect.w > other.x,
        );
        if (nextRowHasWidget) break;
        rect.h += 1;
        if (rect.h > 9 && rect.w < columns) break;
      }
    });
  grid.batchUpdate();
  rects.forEach((rect) => {
    const widget = findWidget(rect.id);
    const item = gridItemForWidget(rect.id);
    if (!widget || !item) return;
    widget.layout = { x: rect.x, y: rect.y, w: rect.w, h: rect.h };
    grid.update(item, widget.layout);
    updateWidgetSizeBadge(item);
    scheduleVisualResize(rect.id);
  });
  grid.batchUpdate(false);
  syncLayoutsFromGrid();
  scheduleGridMetricsUpdate();
  markDirty();
  setStatus('Автоконфигурация перестроила и растянула виджеты', 'success');
}

gridMetricsObserver?.observe(controls.gridRoot);
// Ширина видимой области холста меняется и без resize окна — при открытии и
// закрытии панелей (с анимацией grid-template-columns); сетка внутри неё
// масштабируется, поэтому её собственный размер этого не покажет.
gridMetricsObserver?.observe(controls.canvasScroll);

let canvasSettleTimer = 0;

/**
 * Окончательный пересчёт после серии resize: раскладка заново (не только
 * при смене числа колонок) и перерисовка всех визуализаций. Промежуточные
 * кадры обрабатывает scheduleGridMetricsUpdate, но при быстром изменении
 * размера окна GridStack и масштаб холста могли разойтись на последнем шаге.
 */
export function scheduleCanvasSettle(delay = 180): void {
  window.clearTimeout(canvasSettleTimer);
  canvasSettleTimer = window.setTimeout(() => {
    canvasSettleTimer = 0;
    setLastAppliedGridColumns(0);
    scheduleGridMetricsUpdate();
  }, delay);
}
